import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Warehouse as WarehouseIcon, Plus, Package, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Warehouses = () => {const { t } = useTranslation("ezparcel");
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    zone_type: 'ambient',
    special_requirements: ''
  });

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ezerpToken');
      // Fetch both hubs and custom zones for a comprehensive view
      const hubRes = await fetch(`${API_URL}/api/ezparcel/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const zoneRes = await fetch(`${API_URL}/api/warehouse/zones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let allData = [];
      if (hubRes.ok) {
        const hubs = await hubRes.json();
        allData = [...hubs.map((w) => ({
          id: w.id,
          name: w.name,
          address: w.address,
          city: w.city,
          status: 'active',
          type: 'Hub',
          capacity: w.capacity || 50000,
          currentLoad: Math.floor((w.utilization || 70) * (w.capacity || 50000) / 100),
          manager: 'Operation Manager',
          workingHours: '24/7'
        }))];
      }

      if (zoneRes.ok) {
        const zones = await zoneRes.json();
        allData = [...allData, ...zones.map((z) => ({
          id: z.id,
          name: z.name,
          address: 'Internal Facility',
          city: 'Zonally Assigned',
          status: 'active',
          type: z.type || 'Zone',
          capacity: z.totalBins || 1000,
          currentLoad: z.usedBins || 0,
          manager: 'Floor Supervisor',
          workingHours: 'Shift A/B'
        }))];
      }
      setWarehouses(allData);
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
      toast.error('Failed to load warehouse data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const handleAddWarehouse = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/warehouse/zones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Warehouse zone added successfully');
        setIsModalOpen(false);
        setFormData({
          name: '',
          code: '',
          zone_type: 'ambient',
          special_requirements: ''
        });
        fetchWarehouses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add warehouse');
      }
    } catch (err) {
      console.error('Add warehouse error:', err);
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("warehousesHubs", "Warehouses & Hubs")}</h1>
          <p className="text-muted-foreground mt-2">{t("monitorWarehouseOperations", "Monitor warehouse operations, capacity, and inventory flow")}

          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
          
          <Plus className="mr-2 h-4 w-4" />{t("addWarehouse", "Add Warehouse")}

        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("addWarehouseHub", "Add Warehouse Hub / Zone")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddWarehouse} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("warehouseName", "Warehouse Name")}</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Northeast Regional Hub" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t("shortCode", "Short Code")}</Label>
                <Input
                  id="code"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="e.g. WH-NE" />
                
              </div>
              <div className="space-y-2">
                <Label htmlFor="zone_type">{t("type", "Type")}</Label>
                <Select
                  value={formData.zone_type}
                  onValueChange={(val) => setFormData({ ...formData, zone_type: val })}>
                  
                  <SelectTrigger id="zone_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ambient">{t("ambient", "Ambient")}</SelectItem>
                    <SelectItem value="cold">{t("coldStorage", "Cold Storage")}</SelectItem>
                    <SelectItem value="frozen">{t("frozen", "Frozen")}</SelectItem>
                    <SelectItem value="hazardous">{t("hazardous", "Hazardous")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="special_requirements">{t("specialRequirements", "Special Requirements")}</Label>
              <Input
                id="special_requirements"
                value={formData.special_requirements}
                onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                placeholder="Optional notes..." />
              
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>{t("cancel", "Cancel")}

              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("adding", "Adding...")}

                </> :

                'Add Warehouse'
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {warehouses.map((warehouse) => {
          const capacityPercentage = warehouse.currentLoad / warehouse.capacity * 100;

          return (
            <Card key={warehouse.id} className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--info)/.1)]">
                      <WarehouseIcon className="h-6 w-6 text-[hsl(var(--info))]" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{warehouse.name}</h3>
                      <p className="text-sm text-muted-foreground">{warehouse.id}</p>
                    </div>
                  </div>
                  <Badge className="status-badge status-completed">
                    {(warehouse.status || 'ACTIVE').toUpperCase()}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("address", "Address")}</p>
                    <p className="text-sm mt-1">{warehouse.address}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">{t("type", "Type")}</p>
                      <p className="font-medium text-sm mt-1">{warehouse.type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t("hours", "Hours")}</p>
                      <p className="font-medium text-sm mt-1">{warehouse.workingHours}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">{t("capacityUsage", "Capacity Usage")}</span>
                      <span className="font-medium">
                        {warehouse.currentLoad.toLocaleString()} / {warehouse.capacity}
                      </span>
                    </div>
                    <Progress value={capacityPercentage} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{capacityPercentage.toFixed(1)}{t("utilized", "% utilized")}</p>
                  </div>

                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground">{t("manager", "Manager")}</p>
                    <p className="text-sm font-medium mt-1">{warehouse.manager}</p>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1">{t("viewDetails", "View Details")}

                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Package className="mr-1 h-3 w-3" />{t("inventory", "Inventory")}

                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}
      </div>
    </div>);

};